import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Card, Row, Col, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import api from '../../api';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';
import PermissaoWrapper from '../../components/PermissaoWrapper';
import { PermissaoEnum } from '../../model/Usuario/Permissao';
import VotacaoViewModel from '../../viewmodel/VotacaoViewModel';
import { toast } from 'react-toastify';
import VotacaoModal from './VotacaoModal';
import { Voto, VotoCandidato } from '../../model/banca/votacao/Voto';
import MembroBanca, { PapelNome, StatusMembroNome } from '../../model/banca/MembroBanca';
import { VinculoNome } from '../../model/Pessoa';
import { TipoBancaNome } from '../../model/banca/Banca';
import { TipoBancaViewModel } from '../../viewmodel/banca/TipoBancaViewModel';
import './VotacaoMembro.css';
import BackendErrorMessage from '../../components/BackendErrorMessage';
import TooltipWrapper from '../../components/TooltipWrapper';
import MidiaModal from '../../components/MidiaModal';
import AutodeclaracaoRacialBadge from '../../components/AutodeclaracaoRacialBadge';
import { getConfigBanca } from '../../utils/tipoBancaConfig';

interface InscricaoEdital {
    editalNome: string;
    inscricao: string;
}

interface CandidatoBancaBasico {
    id?: string;
    nome: string;
    nomeSocial?: string;
    inscricao: string;
    editais?: InscricaoEdital[];
    autodeclaracaoRacial?: string;
}

interface VotoInfo {
    id: string;
    voto: string;
    justificativa?: string;
    dataHoraVotacao: string;
}

interface EstatisticasVotacao {
    votosRealizados: number;
    naoIniciados: number;
    fechados: number;
    ausentes: number;
    validados: number;
    naoValidados: number;
}

interface VotacaoMembroResponse {
    aberta?: {
        candidato: CandidatoBancaBasico;
    };
    voto?: VotoInfo;
    estatisticas: EstatisticasVotacao;
}

const VotacaoMembro: React.FC = () => {
    const { bancaId } = useParams<{ bancaId: string }>();
    const { setBreadcrumbs } = useBreadcrumbs();

    const [dados, setDados] = useState<VotacaoMembroResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showVotacaoModal, setShowVotacaoModal] = useState(false);
    const [isVotando, setIsVotando] = useState(false);
    const [isAtualizando, setIsAtualizando] = useState(false);
    const [tipoBanca, setTipoBanca] = useState<TipoBancaNome | null>(null);
    const [showMidiaModal, setShowMidiaModal] = useState(false);
    const [midiaUrl, setMidiaUrl] = useState<string | null>(null);
    const [tipoMidia, setTipoMidia] = useState<'video' | 'image'>('video');
    const [tituloMidiaModal, setTituloMidiaModal] = useState('Mídia do Candidato');
    const [fotoDisponivelRecursal, setFotoDisponivelRecursal] = useState(false);

    const configBanca = useMemo((): Partial<ReturnType<typeof getConfigBanca>> => 
        tipoBanca ? getConfigBanca(tipoBanca) : {}, 
        [tipoBanca]
    );
    const isBancaRecursal = tipoBanca === TipoBancaNome.RECURSAL;

    useEffect(() => {
        setBreadcrumbs([
            { path: '/', breadcrumb: '🏠' },
            { path: `/bancas/${bancaId}/votacao-membro`, breadcrumb: 'Heteroidentificação' },
        ]);
    }, [bancaId, setBreadcrumbs]);

    const carregarTipoBanca = async () => {
        if (!bancaId) return;
        try {
            const tipo = await TipoBancaViewModel.getTipoBanca(bancaId);
            setTipoBanca(tipo.nome);
        } catch (error) {
            console.error('Erro ao carregar tipo da banca:', error);
        }
    };

    useEffect(() => {
        carregarTipoBanca();
    }, [bancaId]);

    const carregarDados = async (silencioso = false) => {
        if (!bancaId) return;

        if (!silencioso) {
            setLoading(true);
        } else {
            setIsAtualizando(true);
        }
        try {
            const response = await api.get(`/bancas/${bancaId}/candidatos/votacao-membro`);
            setDados(response.data);
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Erro ao carregar dados');
        } finally {
            setLoading(false);
            setIsAtualizando(false);
        }
    };

    useEffect(() => {
        carregarDados();
    }, [bancaId]);

    useEffect(() => {
        const interval = setInterval(() => {
            carregarDados(true);
        }, 20000);

        return () => clearInterval(interval);
    }, [bancaId]);

    const verificarFotoRecursalDisponivel = async (candidatoId?: string) => {
        if (!bancaId || !candidatoId || !isBancaRecursal) {
            setFotoDisponivelRecursal(false);
            return;
        }

        try {
            const response = await api.get(`/bancas/${bancaId}/candidatos/${candidatoId}/fotoUrl`);
            setFotoDisponivelRecursal(Boolean(response.data?.url));
        } catch {
            setFotoDisponivelRecursal(false);
        }
    };

    useEffect(() => {
        void verificarFotoRecursalDisponivel(dados?.aberta?.candidato.id);
    }, [bancaId, isBancaRecursal, dados?.aberta?.candidato.id]);

    const formatarDataHora = (dataHora: string) => {
        return new Date(dataHora).toLocaleString('pt-BR');
    };

    const mapearVoto = (voto: string) => {
        switch (voto) {
            case 'VALIDADO':
                return 'Validado';
            case 'INCONCLUSIVO':
                return 'Inconclusivo';
            case 'NAO_VALIDADO':
                return 'Não Validado';
            default:
                return voto;
        }
    };

    const handleVotar = () => {
        setShowVotacaoModal(true);
    };

    const handleConfirmarVoto = async (resultado: VotoCandidato, justificativa: string) => {
        const candidatoId = dados?.aberta?.candidato.id;
        if (!candidatoId) {
            toast.error('Não foi possível identificar o candidato em votação.');
            return;
        }

        const votante: MembroBanca = {
            id: '',
            papelMembro: { id: '', nome: PapelNome.TITULAR },
            statusMembro: StatusMembroNome.DEFINIDO,
            pessoa: {
                id: 'x',
                vinculo: { id: 'x', nome: VinculoNome.DOCENTE },
                nome: 'x',
                email: 'x',
                cpf: 'x',
                isMembroCavu: true
            }
        };

        const voto: Voto = {
            votante,
            resultado,
            justificativa,
            data: new Date(),
        };

        setIsVotando(true);
        try {
            await VotacaoViewModel.postVoto(bancaId!, candidatoId, voto);
            toast.success('Voto registrado');
            carregarDados();
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        } finally {
            setIsVotando(false);
            setShowVotacaoModal(false);
        }
    };

    const handleViewMidia = async (
        candidato: CandidatoBancaBasico,
        customMidia?: {
            endpoint: (bancaId: string, candidatoId: string) => string;
            tipo: 'video' | 'image';
            label: string;
        }
    ) => {
        if (!bancaId || !candidato.id) return;
        const endpointMidia = customMidia?.endpoint || configBanca.endpointMidia;
        if (!endpointMidia) return;
        const tipoMidiaSelecionada = customMidia?.tipo || configBanca.tipoMidia || 'video';
        const labelMidiaSelecionada = customMidia?.label || configBanca.labelMidia || 'Mídia';
        
        try {
            const endpoint = endpointMidia(bancaId, candidato.id);
            const response = await api.get(endpoint);
            if (response.data.url) {
                setMidiaUrl(response.data.url);
                setTipoMidia(tipoMidiaSelecionada);
                setTituloMidiaModal(`${labelMidiaSelecionada} do Candidato`);
                setShowMidiaModal(true);
            } else {
                toast.warning(`${labelMidiaSelecionada} não disponível para este candidato`);
            }
        } catch (error) {
            toast.error(BackendErrorMessage(error));
        }
    };

    const handleCloseMidiaModal = () => {
        setShowMidiaModal(false);
        if (midiaUrl) {
            URL.revokeObjectURL(midiaUrl);
            setMidiaUrl(null);
        }
    };

    if (loading) {
        return (
            <Container className="text-center my-5">
                <Spinner animation="border" />
                <p className="mt-2">Carregando dados da heteroidentificação...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="my-5">
                <Alert variant="danger">
                    <Alert.Heading>Erro</Alert.Heading>
                    <p>{error}</p>
                </Alert>
            </Container>
        );
    }

    if (!dados) return null;

    return (
        <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.VOTAR_CANDIDATO]} bancaIdFromComponent={bancaId!}>
            <Container className="my-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    {isAtualizando && (
                        <div className="text-muted small d-flex align-items-center">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Atualizando...
                        </div>
                    )}
                </div>

                {/* Estatísticas */}
                <Card className="mb-4">
                    <Card.Header>
                        <h5 className="mb-0">Estatísticas Gerais</h5>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={2}>
                                <div className="text-center">
                                    <h4 className="text-warning">{dados.estatisticas.naoIniciados}</h4>
                                    <small>Não Iniciados</small>
                                </div>
                            </Col>
                            <Col md={2}>
                                <div className="text-center">
                                    <h4 className="text-secondary">{dados.estatisticas.fechados}</h4>
                                    <small>Finalizados</small>
                                </div>
                            </Col>
                            <Col md={1}>
                                <div className="d-flex justify-content-center align-items-center h-100">
                                    <div className="border-start border-2 border-muted" style={{ height: '60px' }}></div>
                                </div>
                            </Col>
                            <Col md={2}>
                                <div className="text-center">
                                    <h4 className="text-danger">{dados.estatisticas.ausentes}</h4>
                                    <small>Ausentes</small>
                                </div>
                            </Col>
                            <Col md={1}>
                                <div className="d-flex justify-content-center align-items-center h-100">
                                    <div className="border-start border-2 border-muted" style={{ height: '60px' }}></div>
                                </div>
                            </Col>
                            <Col md={2}>
                                <div className="text-center">
                                    <h4 className="text-success">{dados.estatisticas.validados}</h4>
                                    <small>Validados</small>
                                </div>
                            </Col>
                            <Col md={2}>
                                <div className="text-center">
                                    <h4 className="text-danger">{dados.estatisticas.naoValidados}</h4>
                                    <small>Não Validados</small>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Candidato em Votação Aberta */}
                <Card>
                    <Card.Header className="bg-info text-white">
                        <h5 className="mb-0">Heteroidentificação em Andamento</h5>
                    </Card.Header>
                    <Card.Body>
                        {dados.aberta ? (
                            <>
                                <Row>
                                    <Col md={8}>
                                        <Card className="mb-2">
                                            <Card.Body className="py-3">
                                                <Row>
                                                    <Col md={8}>
                                                        <h3 className="mb-1">
                                                            {dados.aberta.candidato.nomeSocial || dados.aberta.candidato.nome}
                                                            <AutodeclaracaoRacialBadge autodeclaracaoRacial={dados.aberta.candidato.autodeclaracaoRacial} />
                                                        </h3>
                                                        {dados.aberta.candidato.nomeSocial && (
                                                            <div className="text-muted mb-2">Nome: {dados.aberta.candidato.nome}</div>
                                                        )}
                                                    </Col>
                                                    <Col md={4} className="text-end">
                                                        <TooltipWrapper
                                                            tooltipContent={
                                                                dados.aberta.candidato.editais && dados.aberta.candidato.editais.length > 0 ? (
                                                                    <div style={{ maxWidth: '300px' }}>
                                                                        {dados.aberta.candidato.editais.map((edital, index) => (
                                                                            <div key={index} style={{ marginBottom: '4px' }}>
                                                                                <strong>{edital.editalNome}</strong>
                                                                                <br />
                                                                                <span>Inscrição: {edital.inscricao}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : dados.aberta.candidato.inscricao
                                                            }
                                                            tooltipPlacement="top"
                                                        >
                                                            <Badge bg="secondary" style={{ cursor: 'pointer' }}>
                                                                Ver Edital/Inscrição
                                                            </Badge>
                                                        </TooltipWrapper>
                                                    </Col>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col md={4} className="text-center">
                                        <h4 className="text-primary">{dados.estatisticas.votosRealizados}</h4>
                                        <small>{dados.estatisticas.votosRealizados == 1 ? "Já votou" : "Já votaram"}</small>
                                        <div className="mt-3">
                                            {configBanca.temBotaoMidia && (
                                                <Button
                                                    variant="dark"
                                                    onClick={() => handleViewMidia(dados.aberta!.candidato)}
                                                    className="me-2"
                                                >
                                                    {configBanca.iconeMidia && React.createElement(configBanca.iconeMidia as React.ComponentType<any>, { className: 'me-1' })}
                                                    {configBanca.labelMidia || 'Mídia'}
                                                </Button>
                                            )}
                                            {isBancaRecursal && fotoDisponivelRecursal && (
                                                <Button
                                                    variant="outline-dark"
                                                    onClick={() =>
                                                        handleViewMidia(dados.aberta!.candidato, {
                                                            endpoint: (idBanca, idCandidato) =>
                                                                `/bancas/${idBanca}/candidatos/${idCandidato}/fotoUrl`,
                                                            tipo: 'image',
                                                            label: 'Foto'
                                                        })
                                                    }
                                                    className="me-2"
                                                >
                                                    Foto
                                                </Button>
                                            )}
                                            <Button
                                                variant="success"
                                                onClick={handleVotar}
                                                disabled={isVotando}
                                                className={!dados.voto ? 'btn-votar-destacado' : ''}
                                            >
                                                {isVotando ? 'Votando...' : (dados.voto ? 'Alterar Voto' : 'Votar')}
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                                {dados.voto && (
                                    <Alert
                                        variant={
                                            dados.voto.voto === "VALIDADO"
                                                ? "success"
                                                : dados.voto.voto === "INCONCLUSIVO"
                                                    ? "secondary"
                                                    : "danger"
                                        }
                                        className="mt-3"
                                    >
                                        <div><strong>Seu voto:</strong> {mapearVoto(dados.voto.voto)}</div>
                                        {dados.voto.justificativa && (
                                            <div><strong>Justificativa:</strong> {dados.voto.justificativa}</div>
                                        )}
                                        <small className="text-muted">
                                            Votado em: {formatarDataHora(dados.voto.dataHoraVotacao)}
                                        </small>
                                    </Alert>
                                )}
                            </>
                        ) : (
                            <p className="text-muted">Nenhuma heteroidentificação em andamento no momento.</p>
                        )}
                    </Card.Body>
                </Card>

                <VotacaoModal
                    show={showVotacaoModal}
                    onHide={() => setShowVotacaoModal(false)}
                    onConfirmar={handleConfirmarVoto}
                    votarEnabled={!isVotando}
                    setVotarEnabled={() => { }}
                    isFacial={tipoBanca === TipoBancaNome.FACIAL}
                />

                <MidiaModal
                    show={showMidiaModal}
                    onHide={handleCloseMidiaModal}
                    midiaUrl={midiaUrl}
                    tipo={tipoMidia}
                    titulo={tituloMidiaModal}
                />
            </Container>
        </PermissaoWrapper>
    );
};

export default VotacaoMembro;
